import { renderMarkdown } from './markdown.js';

const LAUNCHER_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>';
const CLOSE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
const RESET_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v6h6"></path><path d="M3.51 8.9A9 9 0 1 1 3 15"></path></svg>';
const SEND_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>';
const MIC_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>';

/**
 * @param {{ bottom: number|null, bottomMobile: number|null }} offsets distance from the viewport bottom in px; null keeps the stylesheet default
 */
export function createUi({ config, strings, voiceSupported, offsets, onSend, onPreset, onReset, onVoiceToggle, onOpenStateChange }) {
    const root = document.createElement('div');
    root.className = 'mdd-chatbot';
    root.style.setProperty('--mdd-primary', config.widget.main_color || '#00b0b0');
    root.style.setProperty('--mdd-secondary', config.widget.secondary_color || '#0070d0');

    if (offsets && offsets.bottom !== null) {
        root.style.setProperty('--mdd-offset-bottom', offsets.bottom + 'px');
    }
    if (offsets && offsets.bottomMobile !== null) {
        root.style.setProperty('--mdd-offset-bottom-mobile', offsets.bottomMobile + 'px');
    }

    const avatarUrl = (config.agent.identity && config.agent.identity.avatar_url) || '';
    const title = config.widget.title || config.agent.name || 'Assistant';

    root.innerHTML =
        '<button type="button" class="mdd-chatbot__launcher" aria-label="' + escape(strings.open || 'Open') + '">' + LAUNCHER_ICON + '</button>' +
        '<div class="mdd-chatbot__panel" role="dialog" aria-label="' + escape(title) + '">' +
            '<div class="mdd-chatbot__header">' +
                (avatarUrl ? '<img class="mdd-chatbot__avatar" src="' + escape(avatarUrl) + '" alt="">' : '') +
                '<span class="mdd-chatbot__title">' + escape(title) + '</span>' +
                '<button type="button" class="mdd-chatbot__icon-btn mdd-chatbot__reset" aria-label="' + escape(strings.reset || 'Reset') + '" title="' + escape(strings.reset || 'Reset') + '">' + RESET_ICON + '</button>' +
                '<button type="button" class="mdd-chatbot__icon-btn mdd-chatbot__close" aria-label="' + escape(strings.close || 'Close') + '" title="' + escape(strings.close || 'Close') + '">' + CLOSE_ICON + '</button>' +
            '</div>' +
            '<div class="mdd-chatbot__messages" role="log" aria-live="polite"><div class="mdd-chatbot__spacer" aria-hidden="true"></div></div>' +
            renderPresetsHtml(config.widget.pre_defined_messages || []) +
            '<form class="mdd-chatbot__footer">' +
                '<textarea class="mdd-chatbot__input" rows="1" placeholder="' + escape(strings.placeholder || '') + '"></textarea>' +
                (voiceSupported
                    ? '<button type="button" class="mdd-chatbot__mic" aria-label="' + escape(strings.voice_start || 'Start voice input') + '" title="' + escape(strings.voice_start || 'Start voice input') + '">' + MIC_ICON + '</button>'
                    : '') +
                '<button type="submit" class="mdd-chatbot__send" aria-label="' + escape(strings.send || 'Send') + '" disabled>' + SEND_ICON + '</button>' +
            '</form>' +
        '</div>';

    document.body.appendChild(root);

    const launcher = root.querySelector('.mdd-chatbot__launcher');
    const panel = root.querySelector('.mdd-chatbot__panel');
    const closeBtn = root.querySelector('.mdd-chatbot__close');
    const resetBtn = root.querySelector('.mdd-chatbot__reset');
    const messagesEl = root.querySelector('.mdd-chatbot__messages');
    // Permanent last child (visually at the top of the column-reverse list) so
    // the messages area never matches a host `:empty` rule.
    const spacerEl = root.querySelector('.mdd-chatbot__spacer');
    const presetsEl = root.querySelector('.mdd-chatbot__presets');
    const form = root.querySelector('.mdd-chatbot__footer');
    const input = root.querySelector('.mdd-chatbot__input');
    const sendBtn = root.querySelector('.mdd-chatbot__send');
    const micBtn = root.querySelector('.mdd-chatbot__mic');

    function setOpen(open) {
        root.classList.toggle('mdd-chatbot--open', open);
        onOpenStateChange && onOpenStateChange(open);
        if (open) {
            setTimeout(() => input.focus(), 60);
        }
    }

    function prepend(el) {
        messagesEl.insertBefore(el, messagesEl.firstChild);
    }

    function renderIntro() {
        if (avatarUrl) {
            const img = document.createElement('img');
            img.className = 'mdd-chatbot__intro-avatar';
            img.src = avatarUrl;
            img.alt = '';
            prepend(img);
        }
        const intro = (config.widget.intro_message || '').trim();
        if (!intro) return;
        const el = document.createElement('div');
        el.className = 'mdd-chatbot__intro';
        el.innerHTML = renderMarkdown(intro);
        prepend(el);
    }

    function addUserBubble(text) {
        const el = document.createElement('div');
        el.className = 'mdd-chatbot__bubble mdd-chatbot__bubble--user';
        el.textContent = text;
        prepend(el);
        return el;
    }

    function addBotBubble() {
        const el = document.createElement('div');
        el.className = 'mdd-chatbot__bubble mdd-chatbot__bubble--bot';
        el.textContent = '';
        prepend(el);
        return el;
    }

    function addErrorBubble(text) {
        const el = document.createElement('div');
        el.className = 'mdd-chatbot__bubble mdd-chatbot__bubble--error';
        el.textContent = text;
        prepend(el);
    }

    function addTypingIndicator() {
        const el = document.createElement('div');
        el.className = 'mdd-chatbot__typing';
        el.innerHTML = '<span></span><span></span><span></span>';
        prepend(el);
        return el;
    }

    function clearMessages() {
        messagesEl.innerHTML = '';
        messagesEl.appendChild(spacerEl);
    }

    function updateSendEnabled() {
        sendBtn.disabled = input.value.trim().length === 0;
    }

    function setBusy(busy) {
        input.disabled = busy;
        sendBtn.disabled = busy || input.value.trim().length === 0;
        input.placeholder = busy ? (strings.typing || '...') : (strings.placeholder || '');
    }

    function autoGrow() {
        input.style.height = 'auto';
        const contentHeight = input.scrollHeight;
        input.style.height = Math.min(contentHeight, 120) + 'px';
        input.style.overflowY = contentHeight > 120 ? 'auto' : 'hidden';
    }

    launcher.addEventListener('click', () => setOpen(true));
    closeBtn.addEventListener('click', () => setOpen(false));
    resetBtn.addEventListener('click', () => {
        if (typeof window.confirm === 'function' && !window.confirm(strings.reset_confirm || 'Clear?')) {
            return;
        }
        onReset && onReset();
    });

    input.addEventListener('input', () => {
        updateSendEnabled();
        autoGrow();
    });

    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!sendBtn.disabled) {
                form.requestSubmit();
            }
        }
    });

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        const text = input.value.trim();
        if (!text) return;
        input.value = '';
        autoGrow();
        onSend && onSend(text);
    });

    if (micBtn) {
        micBtn.addEventListener('click', () => {
            if (onVoiceToggle) onVoiceToggle();
        });
    }

    if (presetsEl) {
        presetsEl.addEventListener('click', (e) => {
            const btn = e.target.closest('.mdd-chatbot__preset');
            if (!btn) return;
            const id = btn.getAttribute('data-preset-id');
            const hasAuto = btn.getAttribute('data-has-auto') === '1';
            const display = btn.getAttribute('data-display') || '';
            onPreset && onPreset({ id, hasAuto, display });
        });
    }

    renderIntro();

    return {
        root,
        setOpen,
        addUserBubble,
        addBotBubble,
        addErrorBubble,
        addTypingIndicator,
        clearMessages,
        renderIntro,
        setBusy,
        hidePresets() {
            if (presetsEl) presetsEl.style.display = 'none';
        },
        showPresets() {
            if (presetsEl) presetsEl.style.display = '';
        },
        setInputValue(value) {
            input.value = value || '';
            updateSendEnabled();
            autoGrow();
        },
        focusInput() {
            input.focus();
        },
        setMicListening(listening) {
            if (!micBtn) return;
            micBtn.classList.toggle('mdd-chatbot__mic--listening', !!listening);
            const label = listening
                ? (strings.voice_stop || 'Stop voice input')
                : (strings.voice_start || 'Start voice input');
            micBtn.setAttribute('aria-label', label);
            micBtn.setAttribute('title', label);
        },
        isOpen() {
            return root.classList.contains('mdd-chatbot--open');
        },
    };
}

function renderPresetsHtml(presets) {
    if (!presets || presets.length === 0) return '';
    const buttons = presets
        .map(
            (p) =>
                '<button type="button" class="mdd-chatbot__preset"' +
                ' data-preset-id="' + escape(p.id) + '"' +
                ' data-has-auto="' + (p.has_auto_response ? '1' : '0') + '"' +
                ' data-display="' + escape(p.display_message) + '"' +
                '>' + escape(p.title || p.display_message) + '</button>'
        )
        .join('');
    return '<div class="mdd-chatbot__presets">' + buttons + '</div>';
}

function escape(str) {
    return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export { renderMarkdown };
